import React, { useState, useMemo } from "react";
import { Calculator, BadgeCheck, HandCoins, ScrollText, LineChart, BadgeIndianRupee, GitCompareArrows, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { EMICalculator } from "../components/EMICalculator";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { LenderCard } from "../components/LenderCard";
import { PERSONAL_LENDERS, BUSINESS_LENDERS, OVERDRAFT_LENDERS } from "../data/lenders";
import { calcEMI, calcSIP, calcFD, calcRD, calcIncomeTax, formatINR, formatLakhs } from "../lib/calculators";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

const TOOLS = [
  { icon: Calculator, label: "EMI Calculator", to: "/tools/emi-calculator", desc: "Calculate monthly EMI for any loan." },
  { icon: BadgeCheck, label: "Eligibility Calculator", to: "/tools/eligibility-calculator", desc: "See how much loan you qualify for." },
  { icon: GitCompareArrows, label: "Compare Loans", to: "/tools/compare-loans", desc: "Side-by-side lender comparison." },
  { icon: HandCoins, label: "Balance Transfer", to: "/tools/balance-transfer-calculator", desc: "Save on interest by transferring." },
  { icon: ScrollText, label: "Part Payment", to: "/tools/part-payment-calculator", desc: "See impact of part-payments." },
  { icon: BadgeIndianRupee, label: "Income Tax", to: "/tools/income-tax-calculator", desc: "Old vs new regime tax." },
  { icon: LineChart, label: "SIP Calculator", to: "/tools/sip-calculator", desc: "Plan your wealth via SIPs." },
  { icon: Wallet, label: "FD / RD Calculator", to: "/tools/fd-rd-calculator", desc: "Maturity value of deposits." },
];

export const ToolsHub = () => (
  <div className="ol-container py-10">
    <Breadcrumbs items={[{ label: "Tools" }]}/>
    <h1 className="mt-4 text-4xl sm:text-5xl font-bold font-heading text-[#273B59]">Tools & Calculators</h1>
    <p className="mt-3 text-[#475569] max-w-xl">Free, accurate financial calculators built to help you decide smarter.</p>
    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {TOOLS.map(t => (
        <Link key={t.to} to={t.to} data-testid={`tool-${t.label.toLowerCase().replace(/\s+/g,'-')}`} className="ol-card-hover bg-white border border-[#E2E8F0] rounded-xl p-6">
          <t.icon className="text-[#1088D4]" size={22}/>
          <h3 className="mt-4 font-bold text-[#273B59] font-heading">{t.label}</h3>
          <p className="mt-1 text-sm text-[#475569]">{t.desc}</p>
        </Link>
      ))}
    </div>
  </div>
);

const PageShell = ({ title, subtitle, children, breadcrumbLabel }) => (
  <div className="ol-container py-10">
    <Breadcrumbs items={[{ label: "Tools", to: "/tools/emi-calculator" }, { label: breadcrumbLabel || title }]}/>
    <h1 className="mt-4 text-3xl sm:text-4xl font-bold font-heading text-[#273B59]">{title}</h1>
    {subtitle && <p className="mt-2 text-[#475569]">{subtitle}</p>}
    <div className="mt-8">{children}</div>
  </div>
);

export const EMICalculatorPage = () => (
  <PageShell title="EMI Calculator" subtitle="Calculate your monthly EMI in seconds.">
    <Tabs defaultValue="personal" className="w-full">
      <TabsList className="bg-[#F7F9FC]">
        <TabsTrigger value="personal">Personal Loan</TabsTrigger>
        <TabsTrigger value="business">Business Loan</TabsTrigger>
        <TabsTrigger value="overdraft">Overdraft</TabsTrigger>
      </TabsList>
      <TabsContent value="personal" className="mt-6"><EMICalculator productLabel="Personal Loan"/></TabsContent>
      <TabsContent value="business" className="mt-6"><EMICalculator productLabel="Business Loan" initialPrincipal={1500000}/></TabsContent>
      <TabsContent value="overdraft" className="mt-6"><EMICalculator productLabel="Overdraft" initialPrincipal={1000000}/></TabsContent>
    </Tabs>
  </PageShell>
);

export const EligibilityCalculatorPage = () => {
  const [income, setIncome] = useState(50000);
  const [oblig, setOblig] = useState(5000);
  const [rate, setRate] = useState(11);
  const [tenure, setTenure] = useState(36);
  const foir = 0.55;
  const maxEMI = Math.max(0, income * foir - oblig);
  const r = rate / 12 / 100;
  const n = tenure;
  const pow = Math.pow(1 + r, n);
  const eligible = (maxEMI * (pow - 1)) / (r * pow);
  return (
    <PageShell title="Loan Eligibility Calculator" subtitle="Estimate how much loan you can qualify for.">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 grid md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <Field label="Monthly Income (₹)" type="number" value={income} onChange={setIncome}/>
          <Field label="Existing EMIs / Obligations (₹)" type="number" value={oblig} onChange={setOblig}/>
          <Field label="Interest Rate (%)" type="number" value={rate} onChange={setRate} step="0.1"/>
          <Field label="Tenure (months)" type="number" value={tenure} onChange={setTenure}/>
        </div>
        <div className="bg-[#EAF5FD] rounded-xl p-6 flex flex-col justify-center text-center">
          <p className="text-xs uppercase tracking-wider text-[#475569]">Estimated Eligible Loan Amount</p>
          <div className="mt-2 text-4xl font-bold ol-gradient-text font-heading">{formatLakhs(eligible)}</div>
          <p className="mt-3 text-xs text-[#475569]">Max EMI you can afford: <span className="font-semibold text-[#273B59]">{formatINR(maxEMI)}</span></p>
          <Link to="/apply" className="mt-5"><Button className="ol-btn-primary w-full">Apply for this Amount</Button></Link>
        </div>
      </div>
    </PageShell>
  );
};

export const BalanceTransferPage = () => {
  const [outstanding, setOutstanding] = useState(500000);
  const [oldRate, setOldRate] = useState(15);
  const [newRate, setNewRate] = useState(11);
  const [remaining, setRemaining] = useState(36);
  const oldEMI = calcEMI(outstanding, oldRate, remaining).emi;
  const newEMI = calcEMI(outstanding, newRate, remaining).emi;
  const savings = (oldEMI - newEMI) * remaining;
  return (
    <PageShell title="Balance Transfer Calculator" subtitle="See how much you can save by switching lenders.">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 grid md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <Field label="Outstanding Loan Amount (₹)" type="number" value={outstanding} onChange={setOutstanding}/>
          <Field label="Current Interest Rate (%)" type="number" step="0.1" value={oldRate} onChange={setOldRate}/>
          <Field label="New Interest Rate (%)" type="number" step="0.1" value={newRate} onChange={setNewRate}/>
          <Field label="Remaining Tenure (months)" type="number" value={remaining} onChange={setRemaining}/>
        </div>
        <div className="bg-gradient-to-br from-[#EAF5FD] to-white rounded-xl p-6 flex flex-col justify-center">
          <div className="grid grid-cols-2 gap-3">
            <Bigstat label="Current EMI" value={formatINR(oldEMI)}/>
            <Bigstat label="New EMI" value={formatINR(newEMI)} accent/>
          </div>
          <div className="mt-5 text-center">
            <p className="text-xs uppercase tracking-wider text-[#475569]">Total Savings</p>
            <div className="mt-1 text-3xl font-bold text-[#16A34A] font-heading">{formatINR(savings)}</div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export const PartPaymentPage = () => {
  const [principal, setPrincipal] = useState(500000);
  const [rate, setRate] = useState(12);
  const [tenure, setTenure] = useState(60);
  const [partPay, setPartPay] = useState(50000);
  const base = calcEMI(principal, rate, tenure);
  const reduced = calcEMI(Math.max(0, principal - partPay), rate, tenure);
  const interestSaved = base.total - (reduced.total + partPay);
  return (
    <PageShell title="Part Payment Calculator" subtitle="See the impact of making a lump-sum part-payment.">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 grid md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <Field label="Original Loan Amount (₹)" type="number" value={principal} onChange={setPrincipal}/>
          <Field label="Interest Rate (%)" type="number" step="0.1" value={rate} onChange={setRate}/>
          <Field label="Tenure (months)" type="number" value={tenure} onChange={setTenure}/>
          <Field label="Part Payment Amount (₹)" type="number" value={partPay} onChange={setPartPay}/>
        </div>
        <div className="bg-[#EAF5FD] rounded-xl p-6 flex flex-col justify-center">
          <div className="grid grid-cols-2 gap-3">
            <Bigstat label="EMI Before" value={formatINR(base.emi)}/>
            <Bigstat label="EMI After" value={formatINR(reduced.emi)} accent/>
          </div>
          <div className="mt-5 text-center">
            <p className="text-xs uppercase tracking-wider text-[#475569]">Estimated Interest Saved</p>
            <div className="mt-1 text-3xl font-bold text-[#16A34A] font-heading">{formatINR(Math.max(0, interestSaved))}</div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export const SIPPage = () => {
  const [monthly, setMonthly] = useState(10000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);
  const { invested, futureValue, returns } = calcSIP(monthly, rate, years);
  const data = [{ name: "Invested", value: invested, color: "#1088D4" }, { name: "Returns", value: returns, color: "#16A34A" }];
  return (
    <PageShell title="Mutual Fund SIP Calculator" subtitle="Plan your wealth creation journey.">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 grid md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <Field label="Monthly SIP Amount (₹)" type="number" value={monthly} onChange={setMonthly}/>
          <Field label="Expected Annual Return (%)" type="number" step="0.5" value={rate} onChange={setRate}/>
          <Field label="Investment Duration (Years)" type="number" value={years} onChange={setYears}/>
        </div>
        <div>
          <div className="h-48"><ResponsiveContainer><PieChart><Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={75}>{data.map((d,i) => <Cell key={i} fill={d.color}/>)}</Pie><Tooltip formatter={(v) => formatINR(v)}/><Legend iconType="circle"/></PieChart></ResponsiveContainer></div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <Bigstat label="Invested" value={formatLakhs(invested)}/>
            <Bigstat label="Returns" value={formatLakhs(returns)}/>
            <Bigstat label="Total" value={formatLakhs(futureValue)} accent/>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export const FDRDPage = () => {
  const [tab, setTab] = useState("fd");
  const [amount, setAmount] = useState(100000);
  const [rate, setRate] = useState(7);
  const [years, setYears] = useState(5);
  const fd = calcFD(amount, rate, years);
  const rd = calcRD(amount, rate, years);
  const res = tab === "fd" ? fd : rd;
  return (
    <PageShell title="FD / RD Calculator" subtitle="Calculate maturity value of fixed and recurring deposits.">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F7F9FC]"><TabsTrigger value="fd">FD Calculator</TabsTrigger><TabsTrigger value="rd">RD Calculator</TabsTrigger></TabsList>
        <TabsContent value="fd" className="mt-6"/>
        <TabsContent value="rd" className="mt-6"/>
      </Tabs>
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 grid md:grid-cols-2 gap-8 mt-6">
        <div className="space-y-5">
          <Field label={tab === "fd" ? "Principal (₹)" : "Monthly Deposit (₹)"} type="number" value={amount} onChange={setAmount}/>
          <Field label="Interest Rate (%)" type="number" step="0.1" value={rate} onChange={setRate}/>
          <Field label="Duration (Years)" type="number" value={years} onChange={setYears}/>
        </div>
        <div className="bg-[#EAF5FD] rounded-xl p-6 flex flex-col justify-center">
          <div className="grid grid-cols-3 gap-2">
            <Bigstat label="Invested" value={formatINR(res.invested)}/>
            <Bigstat label="Interest" value={formatINR(res.interest)}/>
            <Bigstat label="Maturity" value={formatINR(res.maturity)} accent/>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export const IncomeTaxPage = () => {
  const [income, setIncome] = useState(1200000);
  const newR = calcIncomeTax(income, "new");
  const oldR = calcIncomeTax(income, "old");
  return (
    <PageShell title="Income Tax Calculator" subtitle="Compare old vs new tax regime for AY 2025-26.">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 grid md:grid-cols-2 gap-8">
        <div>
          <Field label="Annual Income (₹)" type="number" value={income} onChange={setIncome}/>
          <p className="mt-3 text-xs text-[#475569]">Calculation is indicative and excludes deductions for the old regime (assumes no Section 80C etc).</p>
        </div>
        <div className="space-y-3">
          <Regime title="New Regime" tax={newR.total} accent={newR.total <= oldR.total}/>
          <Regime title="Old Regime (no deductions)" tax={oldR.total} accent={oldR.total < newR.total}/>
          <div className="text-sm text-[#16A34A] font-semibold text-center mt-2">
            Recommended: {newR.total <= oldR.total ? "New Regime" : "Old Regime"} (saves {formatINR(Math.abs(newR.total - oldR.total))})
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export const CompareLoansPage = () => {
  const [type, setType] = useState("personal");
  const list = useMemo(() => ({ personal: PERSONAL_LENDERS, business: BUSINESS_LENDERS, overdraft: OVERDRAFT_LENDERS }[type]), [type]);
  const basePath = { personal: "/personal-loan", business: "/business-loan", overdraft: "/overdraft-loan" }[type];
  return (
    <PageShell title="Compare Loans" subtitle="Compare loan offers across India's leading lenders." breadcrumbLabel="Compare Loans">
      <div className="flex items-center gap-3 mb-6">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="compare-type" className="w-56 bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="personal">Personal Loan</SelectItem>
            <SelectItem value="business">Business Loan</SelectItem>
            <SelectItem value="overdraft">Flexi Overdraft</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-[#475569]">{list.length} lenders shown</span>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((s,i) => <LenderCard key={s} slug={s} product={type} basePath={basePath} index={i}/>)}
      </div>
    </PageShell>
  );
};

const Field = ({ label, value, onChange, type = "text", step }) => (
  <div>
    <label className="text-sm font-medium text-[#273B59] mb-1.5 block">{label}</label>
    <Input type={type} step={step} value={value} onChange={(e) => onChange(Number(e.target.value) || 0)} className="bg-white"/>
  </div>
);
const Bigstat = ({ label, value, accent }) => (
  <div className={`rounded-lg p-3 text-center ${accent ? "bg-[#1088D4] text-white" : "bg-white border border-[#E2E8F0] text-[#273B59]"}`}>
    <div className={`text-[10px] uppercase tracking-wider ${accent ? "text-white/80" : "text-[#94A3B8]"}`}>{label}</div>
    <div className="mt-1 text-sm font-bold">{value}</div>
  </div>
);
const Regime = ({ title, tax, accent }) => (
  <div className={`rounded-lg p-5 border ${accent ? "bg-[#EAF5FD] border-[#1088D4]" : "bg-[#F7F9FC] border-[#E2E8F0]"}`}>
    <div className="text-sm font-semibold text-[#273B59]">{title}</div>
    <div className={`mt-1 text-2xl font-bold font-heading ${accent ? "text-[#1088D4]" : "text-[#273B59]"}`}>{formatINR(tax)}</div>
  </div>
);
